import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
    Button,
    alpha,
    IconButton,
    Pagination, // 💡 Ne pas oublier d'importer Pagination
    Avatar
} from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PaymentsIcon from '@mui/icons-material/Payments';
import StorageIcon from '@mui/icons-material/Storage';

// --- CONFIGURATION DES STATUTS (Mapping avec l'API) ---
const STATUS_MAP = {
    'BROUILLON': { label: 'Brouillon', bg: '#9e9e9e', color: '#fff' },
    'EMISE': { label: 'Émise', bg: '#c9a84c', color: '#fff' },
    'PAYEE': { label: 'Payée', bg: '#2e7d32', color: '#fff' },
    'ANNULEE': { label: 'Annulée', bg: '#d32f2f', color: '#fff' },
    'EN_RETARD': { label: 'En retard', bg: '#ef6c00', color: '#fff' },
};

// 💡 Le composant accepte maintenant les données et les fonctions de contrôle en Props
export function FacturesTable({
    data = [],
    page = 0,
    totalPages = 0,
    onPageChange,
    currentStatus = 'ALL',
    onStatusChange
}) {

    // Les onglets qui piloteront la requête API
    const tabs = [
        { id: 'ALL', label: 'Toutes' },
        { id: 'BROUILLON', label: 'Brouillons' },
        { id: 'EMISE', label: 'Émises' },
        { id: 'PAYEE', label: 'Payées' },
        { id: 'ANNULEE', label: 'Annulées' }
    ];

    console.log(data);

    // Utilitaire pour formater les dates ISO du backend
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    return (
        <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', minHeight: 550, display: 'flex', flexDirection: 'column' }}>

            {/* --- HEADER & FILTRES --- */}
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", mb: 4 }}>
                <Box>
                    <Typography variant="h5" color="secondary.main">Factures</Typography>
                </Box>
                <Box>
                    <Stack direction="row" spacing={1} sx={{ bgcolor: alpha('#000', 0.02), p: 0.5, borderRadius: '20px' }}>
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                // On informe le parent qu'on a cliqué sur un statut
                                onClick={() => onStatusChange && onStatusChange(tab.id)}
                                size='small'
                                sx={{
                                    height: '30px',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    color: currentStatus === tab.id ? 'secondary.main' : 'text.secondary',
                                    bgcolor: currentStatus === tab.id ? '#efebe2' : 'transparent',
                                    '&:hover': { bgcolor: alpha('#c9a84c', 0.1) },
                                    boxShadow: currentStatus === tab.id ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                    textTransform: 'none',
                                    fontWeight: currentStatus === tab.id ? 700 : 500
                                }}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </Stack>
                </Box>
            </Box>

            {/* --- TABLEAU --- */}
            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            {['N° FACTURE', 'AGENT', 'MONTANT PAYÉ', 'RESTE À PAYER', 'TOTAL TTC', 'STATUT', 'ÉCHÉANCE', 'ACTIONS'].map((head) => (
                                <TableCell key={head} sx={{
                                    fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary',
                                    letterSpacing: 1, borderBottom: '1px solid rgba(0,0,0,0.05)'
                                }}>
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length === 0 ? (
                            <TableRow sx={{ height: 200 }}>
                                <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "auto"
                                    }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'secondary.main', color: 'primary.main',
                                                width: 60, height: 60,
                                                fontSize: '2rem', fontWeight: 700,
                                                border: '3px solid', borderColor: alpha('#c9a84c', 0.3),
                                                mx: 'auto', mb: 2
                                            }}>
                                            <StorageIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="body2">Aucune facture trouvée.</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => {
                                // Récupération du style du statut, fallback sur noir si inconnu
                                const statusStyle = STATUS_MAP[row.statut] || { label: row.statut, bg: '#000', color: '#fff' };

                                return (
                                    <TableRow key={row.id} sx={{ '&:hover': { bgcolor: alpha('#c9a84c', 0.02) } }}>
                                        <TableCell sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.85rem' }}>
                                            {row.numeroFacture || 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{row.agentUsername}</TableCell>
                                        <TableCell color="text.secondary">{Number(row.montantPaye)?.toLocaleString('fr-FR')}</TableCell>
                                        <TableCell color="text.secondary">{(Number(row.montantTotal) - Number(row.montantPaye))?.toLocaleString('fr-FR')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{Number(row.montantTotal)?.toLocaleString('fr-FR')}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={statusStyle.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    fontWeight: 700, fontSize: '0.65rem', borderRadius: '4px',
                                                    height: 24, minWidth: '70px'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            {formatDate(row.dateEcheance)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton size="small" title="Télécharger">
                                                    <FileDownloadIcon sx={{ fontSize: '1rem', color: "primary.main" }} />
                                                </IconButton>
                                                <IconButton size="small" title="Modifier">
                                                    <BorderColorIcon sx={{ fontSize: '1rem', color: "secondary.main" }} />
                                                </IconButton>
                                                <IconButton size="small" title="Paiements">
                                                    <PaymentsIcon sx={{ fontSize: '1rem', color: "secondary.main" }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* --- PAGINATION --- */}
            {totalPages > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <Pagination
                        count={totalPages}
                        page={page + 1} // MUI commence à 1, Spring commence à 0
                        onChange={(event, value) => onPageChange(value - 1)}
                        color="secondary"
                        shape="rounded"
                    />
                </Box>
            )}
        </Paper>
    );
}